import type React from "react";
import { useState } from "react";

import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { faReceipt } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Collapse, IconButton, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useGetBillingPortalUrl, useGetInvoices } from "api/services/admin/hooks";
import type { Invoice } from "api/services/admin/schemas";
import { BaseButton } from "shared/atoms/BaseButton";
import { WidgetBox } from "shared/containers/WidgetBox";

import { InvoicePreviewModal } from "./InvoicePreviewModal";
import { InvoiceSpecificationListTable } from "./InvoiceSpecificationListTable";

interface Props {
  hcoID: number;
}

export const InvoiceSpecificationList: React.FC<Props> = ({ hcoID }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const { data: billingPortalData } = useGetBillingPortalUrl({
    health_care_organization_id: hcoID,
  });
  const portalUrl = billingPortalData?.billing_portal_url;

  const { data: invoicesData, isLoading } = useGetInvoices(
    { health_care_organization_id: hcoID },
    { enabled: !!hcoID }
  );
  const invoices = (invoicesData?.invoices ?? []).filter(inv => inv.status === "paid" || inv.status === "open");

  return (
    <>
      <WidgetBox
        title={t("admin.invoice_specification.title")}
        icon={<FontAwesomeIcon icon={faReceipt} />}
        color="blue"
        skipPadding
        toolbar={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <BaseButton
              variant="action"
              text={t("admin.subscription.manage_subscription")}
              disabled={!portalUrl}
              onClick={() => portalUrl && window.open(portalUrl, "_blank")}
            />
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} style={{ fontSize: 14 }} />
            </IconButton>
          </Stack>
        }
      >
        <Collapse in={expanded}>
          <InvoiceSpecificationListTable
            invoices={invoices}
            isLoading={isLoading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            onPreview={invoice => setSelectedInvoice(invoice)}
          />
        </Collapse>
      </WidgetBox>

      {selectedInvoice && (
        <InvoicePreviewModal invoice={selectedInvoice} hcoID={hcoID} onClose={() => setSelectedInvoice(null)} />
      )}
    </>
  );
};
